import React, { useEffect, useState } from 'react'
import {
  CircularProgress,
  Popover,
  Typography,
  Grid,
  Paper
} from '@mui/material'
import {
  type PersonBaseBasicInformation,
  type PersonBackgroundInfoResponse, usGetPersonBasicInfo, getPersonBackgroundInfo,
  usGetLegislativeRoles, usGetExecutiveRoles, type ExecutivePosition,
  type ExecutiveStafferRole, type ExecutiveRolesForPerson, type ExecOrg
} from 'services'
import { type State } from 'components/us/common/state-select'
import { useUsPrefixLuts } from 'components'
import { usPoliticalPartiesLutValueList } from 'services/lutValuesService'
import {
  ethnicityOptions,
  genderOptions,
  maritalStatusOptions,
  religionOptions,
  suffixOptions
} from 'utils'
import { type LegislativeRoles } from 'services/person/us/edit/legislativeRolesService'
import { getOfficeFromLeadershipStaffRole, getMemberFromStaffRole } from 'helpers/person/personHelper'
import { commentService, type KwComment } from 'services/us/commentService'

interface PersonDetailsPopoverProps {
  personId: number
  anchorEl: HTMLElement | null
  onClose: () => void
}

interface PersonDetailItem {
  label: string
  value: string | null
}

const FEDERAL_STATE_ID = 0

const formatOrgChain = (org: ExecOrg): string =>
  [org.parentOrg?.name, org.name].filter(Boolean).join(' - ')

const formatJurisdiction = (state: State): string | undefined =>
  state.id === FEDERAL_STATE_ID ? 'Federal' : state.desc

const formatExecutivePosition = (position: ExecutivePosition): string => [
  position.title,
  formatOrgChain(position.org),
  position.execRole.descShort,
  formatJurisdiction(position.state)
].filter(Boolean).join(' | ')

const formatExecutiveStafferRole = (role: ExecutiveStafferRole): string => [
  role.title,
  formatOrgChain(role.position.org),
  role.execRole.descShort,
  `Staff to ${role.position.title}`,
  formatJurisdiction(role.position.state)
].filter(Boolean).join(' | ')

export const PersonDetailsPopover: React.FC<PersonDetailsPopoverProps> = ({
  personId,
  anchorEl,
  onClose
}) => {
  const { lutValues: usPrefixLutValues } = useUsPrefixLuts()
  const [loading, setLoading] = useState<boolean>(true)
  const [basicInfo, setBasicInfo] = useState<PersonBaseBasicInformation | null>(null)
  const [backgroundInfo, setBackgroundInfo] = useState<PersonBackgroundInfoResponse | null>(null)
  const [legislativeRoles, setLegislativeRoles] = useState<LegislativeRoles | null>(null)
  const [executiveRoles, setExecutiveRoles] = useState<ExecutiveRolesForPerson>({ positions: [], stafferRoles: [] })
  const [comment, setComment] = useState<KwComment | null>(null)
  const [politicalParties, setPoliticalParties] = useState<Array<{ id: number, desc: string }>>([])
  const [error, setError] = useState<string | null>(null)

  const formatOccupation = ({
    occupation,
    company,
    details,
    startDate,
    endDate
  }: {
    occupation: string | null
    company: string | null
    details: string | null
    startDate: string | null
    endDate: string | null
  }): string => {
    const description = [occupation, company, details].filter(Boolean).join(', ')
    const dateRange = [startDate, endDate?.replace('CURR', 'Current')].filter(Boolean).join(' - ')
    return dateRange !== '' ? `${description} (${dateRange})` : description
  }

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      if ((personId !== 0) && (anchorEl != null)) {
        setLoading(true)
        setError(null)

        try {
          const basicInfoPromise = usGetPersonBasicInfo(personId)
          const backgroundInfoPromise = getPersonBackgroundInfo(personId)
          const legislativeRolesPromise = usGetLegislativeRoles(personId)
          const executiveRolesPromise = usGetExecutiveRoles(personId)
          const politicalPartiesPromise = usPoliticalPartiesLutValueList()
          const commentPromise = commentService.getComment('P', personId).catch(() => null)

          const [basicInfoData, backgroundInfoData, legislativeRolesData, executiveRolesData, politicalPartiesData, commentData] = await Promise.all([
            basicInfoPromise,
            backgroundInfoPromise,
            legislativeRolesPromise,
            executiveRolesPromise,
            politicalPartiesPromise,
            commentPromise
          ])

          setBasicInfo(basicInfoData)
          setBackgroundInfo(backgroundInfoData.result)
          setLegislativeRoles(legislativeRolesData)
          setExecutiveRoles(executiveRolesData)
          setPoliticalParties(politicalPartiesData.parties)
          setComment(commentData)
        } catch (err) {
          setError('Failed to load person details')
          console.error('Error fetching person details:', err)
        } finally {
          setLoading(false)
        }
      }
    }
    void fetchData()
  }, [personId])

  const getDetailItems = (): PersonDetailItem[] => {
    if (basicInfo == null) return []

    const items: PersonDetailItem[] = [
      {
        label: 'Full Name',
        value: `${usPrefixLutValues.prefixes.find(prefix => prefix.id === basicInfo.prefixId)?.code ?? ''} ${basicInfo.firstName ?? ''} ${basicInfo.middleName ?? ''} ${basicInfo.lastName ?? ''} ${suffixOptions.find(s => s.id === basicInfo.suffixId)?.value ?? ''}`.trim()
      },
      { label: 'Prof Suffix', value: basicInfo.profSuffix ?? null },
      { label: 'Legal Name', value: basicInfo.legalName ?? null },
      { label: 'Nickname', value: basicInfo.nickname ?? null },
      { label: 'Pronunciation', value: basicInfo.pronunciation ?? null },
      {
        label: 'Ethnicity',
        value: ethnicityOptions.find(e => e.id === basicInfo.ethnicId)?.value ?? null
      },
      {
        label: 'Gender',
        value: genderOptions.find(g => g.id === basicInfo.genderId)?.value ?? null
      },
      {
        label: 'Political Party',
        value: basicInfo.partyId != null && basicInfo.partyId !== 0
          ? politicalParties.find(p => p.id === basicInfo.partyId)?.desc ?? `Party ID: ${basicInfo.partyId}`
          : null
      },
      {
        label: 'Religion',
        value: religionOptions.find(r => r.id === basicInfo.religionId)?.value ?? null
      },
      {
        label: 'Marital Status',
        value: maritalStatusOptions.find(m => m.id === basicInfo.maritalStatusId)?.value ?? null
      },
      { label: 'Spouse Name', value: basicInfo.spouseName ?? null },
      { label: 'Family', value: basicInfo.family ?? null },
      { label: 'Residence', value: basicInfo.residence ?? null },
      {
        label: 'Birthplace',
        value: basicInfo.birthplace ?? null
      },
      {
        label: 'Birthdate',
        value: basicInfo.birthYear != null
          ? `${basicInfo.birthMonth ?? ''}/${basicInfo.birthDate ?? ''}/${basicInfo.birthYear}`
          : null
      },
      {
        label: 'Death date',
        value: basicInfo.deathYear != null
          ? `${basicInfo.deathMonth ?? ''}/${basicInfo.deathDate ?? ''}/${basicInfo.deathYear}`
          : null
      }
    ]

    const demographicItemCount = items.length

    // Add roles if available
    if (legislativeRoles != null) {
      // Member roles (legislators)
      if (legislativeRoles.member_roles?.length > 0) {
        items.push({
          label: 'Member Roles',
          value: legislativeRoles.member_roles.map(role => {
            const parts: string[] = [role.legrole?.descLong ?? 'Member']
            const districtLabel = (role.district?.district_code != null && role.district.district_code !== '') ? `District ${role.district.district_code}` : null
            const location = [role.state?.desc, districtLabel].filter(Boolean).join(', ')
            if (location !== '') parts.push(location)
            if (role.majmin != null && role.majmin !== '') parts.push(role.majmin)
            const party = basicInfo?.partyId != null && basicInfo.partyId !== 0
              ? politicalParties.find(p => p.id === basicInfo.partyId)?.desc ?? null
              : null
            if (party != null) parts.push(party)
            return parts.join(' | ')
          }).join('; ')
        })
      }

      // Committee memberships
      if (legislativeRoles.committee_members?.length > 0) {
        items.push({
          label: 'Committee Memberships',
          value: legislativeRoles.committee_members.map(cm => {
            const name = cm.committee?.name ?? 'Committee'
            const roleDesc = cm.leg_role?.descLong ?? cm.role?.desc ?? null
            return roleDesc != null ? `${name} (${roleDesc})` : name
          }).join('; ')
        })
      }

      // Leadership roles
      if (legislativeRoles.leadership_roles?.length > 0) {
        items.push({
          label: 'Leadership Roles',
          value: legislativeRoles.leadership_roles.map(role => {
            const parts: string[] = [role.ofcname ?? role.role?.desc ?? 'Leadership']
            const context = [role.legchamber?.desc, role.state?.desc].filter(Boolean).join(', ')
            if (context !== '') parts.push(context)
            return parts.join(' — ')
          }).join('; ')
        })
      }

      // Caucus memberships
      if (legislativeRoles.caucus_members?.length > 0) {
        items.push({
          label: 'Caucus Memberships',
          value: legislativeRoles.caucus_members.map(cm => {
            const name = cm.committee?.name ?? 'Caucus'
            const roleDesc = cm.leg_role?.descLong ?? cm.role?.desc ?? null
            return roleDesc != null ? `${name} (${roleDesc})` : name
          }).join('; ')
        })
      }

      // Member staff roles
      if (legislativeRoles.member_staff_roles?.length > 0) {
        items.push({
          label: 'Member Staff Roles',
          value: legislativeRoles.member_staff_roles.map(role =>
            `${String(role.legrole.descLong ?? 'Staff')} for ${getMemberFromStaffRole(role)}`
          ).join('; ')
        })
      }

      // Committee staff roles
      if (legislativeRoles.committee_staff_roles?.length > 0) {
        items.push({
          label: 'Committee Staff Roles',
          value: legislativeRoles.committee_staff_roles.map(role =>
            `${String(role.legrole.descLong ?? 'Staff')} for ${String(role.committee?.name ?? 'Committee')}`
          ).join('; ')
        })
      }

      // Leadership staff roles
      if (legislativeRoles.leadership_staff_roles?.length > 0) {
        items.push({
          label: 'Leadership Staff Roles',
          value: legislativeRoles.leadership_staff_roles.map(role => {
            return `${String(role.legrole?.descLong ?? 'Staff')} for ${getOfficeFromLeadershipStaffRole(role)}`
          }).join('; ')
        })
      }
    }

    if (executiveRoles.positions.length > 0) {
      items.push({
        label: 'Executive Positions',
        value: executiveRoles.positions.map(formatExecutivePosition).join('; ')
      })
    }

    if (executiveRoles.stafferRoles.length > 0) {
      items.push({
        label: 'Executive Staff Roles',
        value: executiveRoles.stafferRoles.map(formatExecutiveStafferRole).join('; ')
      })
    }

    if (items.length === demographicItemCount) {
      items.push({
        label: 'Current Roles',
        value: 'No current legislative or executive roles'
      })
    }

    // Add background info items if available
    if (backgroundInfo != null) {
      // Awards
      if (backgroundInfo.awards != null && backgroundInfo.awards.length > 0) {
        items.push({
          label: 'Awards',
          value: backgroundInfo.awards.map(a => a.awardTitle).join(', ')
        })
      }

      // Board of Directors
      if (backgroundInfo.directors != null && backgroundInfo.directors.length > 0) {
        items.push({
          label: 'Boards of Directors',
          value: backgroundInfo.directors.map(d => `${d.company} (${d.title})`).join(', ')
        })
      }

      // Hobbies and Interests
      if (backgroundInfo.interests != null && backgroundInfo.interests.length > 0) {
        items.push({
          label: 'Hobbies and Interests',
          value: backgroundInfo.interests.map(i => i.interest).join(', ')
        })
      }

      // Military Service
      if (backgroundInfo.military_service != null && backgroundInfo.military_service.length > 0) {
        items.push({
          label: 'Military Service',
          value: backgroundInfo.military_service.map(m => `${m.branch} (${m.highestRank ?? ''})`).join(', ')
        })
      }

      // Notable Facts
      if (backgroundInfo.facts != null && backgroundInfo.facts.length > 0) {
        items.push({
          label: 'Notable Facts',
          value: backgroundInfo.facts.map(f => f.factText).join('; ')
        })
      }

      // Organization Memberships
      if (backgroundInfo.memberships != null && backgroundInfo.memberships.length > 0) {
        items.push({
          label: 'Organization Memberships',
          value: backgroundInfo.memberships.map(m => m.organization).join(', ')
        })
      }

      // Political Experience
      if (backgroundInfo.experience != null && backgroundInfo.experience.length > 0) {
        items.push({
          label: 'Political Experience',
          value: backgroundInfo.experience.map(e => `${e.organization ?? ''} (${e.title ?? ''})`).join(', ')
        })
      }

      // Previous Occupations
      if (backgroundInfo.occupations != null && backgroundInfo.occupations.length > 0) {
        items.push({
          label: 'Previous Occupations',
          value: backgroundInfo.occupations.map(formatOccupation).join('; ')
        })
      }

      // Previous Comments
      if (comment?.text != null && comment.text.trim() !== '') {
        items.push({
          label: 'Previous Comments',
          value: comment.text
        })
      }

      // Publication Credits
      if (backgroundInfo.publications != null && backgroundInfo.publications.length > 0) {
        items.push({
          label: 'Publication Credits',
          value: backgroundInfo.publications.map(p => p.pubTitle).join(', ')
        })
      }
    }

    const sortedItems = items
      .filter(item => item.value !== null)
      .sort((a, b) => Number(Boolean(b.value)) - Number(Boolean(a.value)))

    return sortedItems
  }

  const open = Boolean(anchorEl)
  const id = open ? 'person-details-popover' : undefined

  return (
    <Popover
      id={id}
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'center',
        horizontal: 'right'
      }}
      transformOrigin={{
        vertical: 'center',
        horizontal: 'left'
      }}
      PaperProps={{
        sx: {
          width: '400px',
          maxHeight: '80vh',
          overflowY: 'auto',
          p: 2
        }
      }}
      disablePortal
      disableScrollLock
    >
      {loading
        ? (
          <CircularProgress />
          )
        : error != null
          ? (
        <Typography color="error">{error}</Typography>
            )
          : (
        <Paper elevation={0}>
          <Typography variant="h6" gutterBottom>
            Person Details
          </Typography>
          <Grid container spacing={2}>
            {getDetailItems().map((item, index) => (
              <React.Fragment key={index}>
                <Grid item xs={5}>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {item.label}:
                  </Typography>
                </Grid>
                <Grid item xs={7}>
                  <Typography variant="body2">
                    {item.value}
                  </Typography>
                </Grid>
              </React.Fragment>
            ))}
          </Grid>
        </Paper>
            )}
    </Popover>
  )
}
